// Generation detail (PX-433) — full view of one generation without opening
// fal.ai. Main = generated result images (large); below = input photos +
// error/metadata. Refund + hard-delete reuse the existing admin routes.

import { useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { APIError, request } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { hasPerm } from '@/lib/perms'
import { Field, firstApiError } from '@/components/FormControls'
import {
  PERM,
  type AdminGenerationDetail,
  type AdminGenerationFalData,
  type AdminRecoverResponse,
  type AdminRefundResponse,
} from '@/types/api'

const STATUS_TONE: Record<string, string> = {
  queued: 'bg-yellow-100 text-yellow-700',
  processing: 'bg-blue-100 text-blue-700',
  completed: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-red-100 text-red-700',
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="shrink-0 text-ink-400">{label}</dt>
      <dd className="text-right text-ink-700 break-all">{children}</dd>
    </div>
  )
}

export function GenerationDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { profile } = useAuth()
  const canWrite = hasPerm(profile, PERM.generations_write)
  const [showRefundModal, setShowRefundModal] = useState(false)

  const q = useQuery({
    queryKey: ['admin', 'generations', 'detail', id],
    queryFn: () => request<AdminGenerationDetail>(`/admin/generations/${id}`),
    enabled: !!id,
  })

  const refund = useMutation({
    mutationFn: ({ amount, reason }: { amount: number; reason: string }) =>
      request<AdminRefundResponse>(`/admin/generations/${id}/refund`, { method: 'POST', body: { amount, reason } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'generations'] })
    },
  })

  const del = useMutation({
    mutationFn: () => request<unknown>(`/admin/generations/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'generations'] })
      navigate('/generations')
    },
  })

  // PX-482: pull a stuck generation's real outcome back from fal and finalise
  // the row (flip → completed + stamp result URLs + re-debit if it was refunded).
  const recover = useMutation({
    mutationFn: () =>
      request<AdminRecoverResponse>(`/admin/generations/${id}/recover`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'generations'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'generations', 'fal', id] })
    },
  })

  if (q.isLoading) return <div className="p-6 text-ink-400">Loading…</div>
  if (q.error || !q.data) {
    return (
      <div className="p-6">
        <Link to="/generations" className="text-sm text-brand-600 hover:underline">← Generations</Link>
        <p className="mt-4 text-ink-500">Generation not found.</p>
      </div>
    )
  }

  const g = q.data
  const canRefund = canWrite && g.credits_used > 0

  return (
    <div className="p-6">
      <Link to="/generations" className="text-sm text-brand-600 hover:underline">← Generations</Link>
      <header className="mt-2 mb-6 flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Generation</h1>
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_TONE[g.status] ?? 'bg-ink-100 text-ink-600'}`}>
          {g.status}
        </span>
        <code className="font-mono text-xs text-ink-400">{g.id}</code>
      </header>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* MAIN: generated result (large), then inputs, then error */}
        <div className="lg:col-span-2">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-ink-400">Result</h2>
          {g.result_video_urls.length > 0 || g.result_urls.length > 0 ? (
            <div className="space-y-3">
              {g.result_video_urls.map((u, i) => (
                <video
                  key={`v-${i}`}
                  src={u}
                  controls
                  playsInline
                  className="w-full rounded-xl border border-ink-100 bg-black"
                />
              ))}
              {g.result_urls.length > 0 && (
                <div className="grid gap-3 sm:grid-cols-2">
                  {g.result_urls.map((u, i) => (
                    <a key={i} href={u} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-xl border border-ink-100 bg-ink-50">
                      <img src={u} alt={`result ${i + 1}`} className="w-full object-cover" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-ink-200 p-6 text-center text-sm text-ink-400">
              No result {g.output_kind === 'video' ? 'video' : 'images'} (status: {g.status}).
            </div>
          )}

          <h2 className="mb-2 mt-6 text-sm font-semibold uppercase tracking-wide text-ink-400">Input photos</h2>
          {g.input_image_urls.length ? (
            <div className="flex flex-wrap gap-2">
              {g.input_image_urls.map((u, i) => (
                <a key={i} href={u} target="_blank" rel="noreferrer" className="block h-20 w-20 overflow-hidden rounded-lg border border-ink-100 bg-ink-50">
                  <img src={u} alt={`input ${i + 1}`} className="h-full w-full object-cover" />
                </a>
              ))}
            </div>
          ) : (
            <p className="text-xs text-ink-400">—</p>
          )}

          {g.input_video_urls.length > 0 && (
            <>
              <h2 className="mb-2 mt-6 text-sm font-semibold uppercase tracking-wide text-ink-400">Input video</h2>
              <div className="space-y-2">
                {g.input_video_urls.map((u, i) => (
                  <video
                    key={i}
                    src={u}
                    controls
                    playsInline
                    className="w-full max-w-sm rounded-lg border border-ink-100 bg-black"
                  />
                ))}
              </div>
            </>
          )}

          {(g.error_code || g.error_message) && (
            <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4">
              <h2 className="text-sm font-semibold text-red-700">Error</h2>
              {g.error_code && <div className="mt-1 font-mono text-xs text-red-700">{g.error_code}</div>}
              {g.error_message && <div className="mt-1 text-sm text-red-600">{g.error_message}</div>}
              {g.error_context && Object.keys(g.error_context).length > 0 && (
                <pre className="mt-2 overflow-x-auto rounded bg-white px-2 py-1 font-mono text-[10px] text-red-700">
                  {JSON.stringify(g.error_context, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>

        {/* SIDE: metadata + actions */}
        <div className="space-y-4">
          <div className="rounded-xl border border-ink-100 bg-white p-4 shadow-sm">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-ink-400">Details</h2>
            <dl className="space-y-2 text-sm">
              <Row label="User">
                <Link to={`/users/${g.user_id}`} className="text-brand-600 hover:underline">{g.user_email ?? g.user_id}</Link>
              </Row>
              <Row label="Photoshoot">{g.photoshoot_slug ?? '—'}</Row>
              <Row label="Model">{g.model_slug ?? '—'}</Row>
              <Row label="Credits used">{g.credits_used}</Row>
              <Row label="fal request"><code className="font-mono text-xs">{g.fal_request_id ?? '—'}</code></Row>
              <Row label="Created">{new Date(g.created_at).toLocaleString()}</Row>
              <Row label="Updated">{new Date(g.updated_at).toLocaleString()}</Row>
            </dl>
          </div>

          {g.prompt_used && (
            <div className="rounded-xl border border-ink-100 bg-white p-4 shadow-sm">
              <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-ink-400">Prompt</h2>
              <p className="whitespace-pre-wrap text-xs text-ink-600">{g.prompt_used}</p>
            </div>
          )}

          {canWrite && (
            <div className="flex flex-wrap gap-2">
              {g.status !== 'completed' && g.fal_request_id && (
                <button
                  type="button"
                  disabled={recover.isPending}
                  onClick={() => {
                    if (
                      confirm(
                        'Pull this generation’s result back from fal and mark it completed?\n\n' +
                          'If the row was already refunded (failed by timeout), its credits will be ' +
                          're-debited so the user is charged for the successful result.',
                      )
                    )
                      recover.mutate()
                  }}
                  className="rounded-md border border-emerald-300 px-3 py-1.5 text-sm font-medium text-emerald-700 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {recover.isPending ? 'Recovering…' : 'Recover from fal'}
                </button>
              )}
              {canRefund && (
                <button
                  type="button"
                  disabled={refund.isPending}
                  onClick={() => setShowRefundModal(true)}
                  className="rounded-md border border-ink-200 px-3 py-1.5 text-sm font-medium text-ink-700 transition hover:border-brand-300 hover:text-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Refund
                </button>
              )}
              <button
                type="button"
                disabled={del.isPending}
                onClick={() => { if (confirm('Hard-delete this generation? This cannot be undone.')) del.mutate() }}
                className="rounded-md border border-red-200 px-3 py-1.5 text-sm font-medium text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Delete
              </button>
            </div>
          )}
          {refund.error instanceof APIError && <div className="text-xs text-red-600">{refund.error.message}</div>}
          {recover.error instanceof APIError && <div className="text-xs text-red-600">{recover.error.message}</div>}
          {recover.data && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
              Recovered ({recover.data.prior_status} → completed).
              {recover.data.redebited
                ? ` Re-debited ${recover.data.redebited_amount} credit(s); new balance ${recover.data.new_credits_balance ?? '—'}.`
                : recover.data.prior_status === 'failed'
                  ? ' Was refunded but re-debit was skipped (insufficient balance) — adjust manually.'
                  : ' No credit change (was not refunded).'}
            </div>
          )}
        </div>
      </div>

      <FalDataSection id={g.id} createdAt={g.created_at} updatedAt={g.updated_at} />

      {showRefundModal && (
        <RefundModal
          maxAmount={g.credits_used}
          pending={refund.isPending}
          error={refund.error instanceof APIError ? refund.error : null}
          onConfirm={(amount, reason) => {
            refund.mutate({ amount, reason })
            setShowRefundModal(false)
          }}
          onCancel={() => setShowRefundModal(false)}
        />
      )}
    </div>
  )
}

// ----- Refund modal -------------------------------------------------
// Same visual pattern as RoleChangeConfirmModal / SubscriptionChangeConfirmModal
// in Users.tsx: fixed backdrop + max-w-md white card + Cancel / Confirm footer.

function RefundModal({
  maxAmount,
  pending,
  error,
  onConfirm,
  onCancel,
}: {
  maxAmount: number
  pending: boolean
  error: APIError | null
  onConfirm: (amount: number, reason: string) => void
  onCancel: () => void
}) {
  const [amount, setAmount] = useState(String(maxAmount))
  const [reason, setReason] = useState('')

  const parsed = Number(amount)
  const amountValid = Number.isFinite(parsed) && Number.isInteger(parsed) && parsed >= 1 && parsed <= maxAmount
  const canSubmit = amountValid && !pending

  const apiErr = firstApiError(error)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6" onClick={onCancel}>
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold tracking-tight">Refund credits?</h2>
        <p className="mt-2 text-sm text-ink-500">
          Enter how many credits to return. The backend enforces refund-once; this call simply surfaces the affordance.
        </p>

        {apiErr && (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {apiErr.message}
          </div>
        )}

        <div className="mt-4 space-y-3">
          <Field
            label={`Amount (1–${maxAmount})`}
            type="number"
            value={amount}
            onChange={setAmount}
          />
          {!amountValid && amount !== '' && (
            <p className="text-xs text-red-600">Must be a whole number between 1 and {maxAmount}.</p>
          )}
          <Field
            label="Reason (audit log)"
            value={reason}
            onChange={setReason}
            placeholder="e.g. generation quality issue"
          />
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="rounded-md border border-ink-200 px-3 py-1.5 text-sm font-medium text-ink-700 transition hover:bg-ink-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => onConfirm(parsed, reason.trim())}
            className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:opacity-60"
          >
            {pending ? 'Refunding…' : `Refund ${amountValid ? parsed : ''}`.trim()}
          </button>
        </div>
      </div>
    </div>
  )
}

// Collapsible "fal.ai" section (PX-459) — lazily fetches live request data from
// fal's queue API (status / inference_time / logs / output description) only when
// expanded. Cost / Runner ID / the timing waterfall aren't in fal's public API
// (dashboard-only), so we surface inference_time + our own created→updated span.
function FalDataSection({
  id,
  createdAt,
  updatedAt,
}: {
  id: string
  createdAt: string
  updatedAt: string
}) {
  const [open, setOpen] = useState(false)
  const q = useQuery({
    queryKey: ['admin', 'generations', 'fal', id],
    queryFn: () => request<AdminGenerationFalData>(`/admin/generations/${id}/fal`),
    enabled: open,
    staleTime: 60_000,
  })
  const ourDurationSec = Math.max(
    0,
    (new Date(updatedAt).getTime() - new Date(createdAt).getTime()) / 1000,
  )

  return (
    <div className="mt-6 rounded-xl border border-ink-100 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-3 text-left"
      >
        <span className="flex flex-wrap items-center gap-2 text-sm font-semibold uppercase tracking-wide text-ink-400">
          <span>fal.ai</span>
          <span className="font-normal normal-case text-ink-300">live data fetched by request_id</span>
        </span>
        <span className="text-ink-400">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="border-t border-ink-100 px-4 py-4">
          {q.isLoading && <p className="text-sm text-ink-400">Loading fal data…</p>}
          {q.error instanceof APIError && (
            <div className="text-sm text-red-600">
              {q.error.message}
              <button type="button" onClick={() => q.refetch()} className="ml-2 underline">
                Retry
              </button>
            </div>
          )}
          {q.data && !q.data.available && (
            <p className="text-sm text-ink-500">
              {q.data.reason === 'no_request_id'
                ? 'This generation never reached fal (no request id).'
                : 'Request data is no longer available on fal (purged past retention).'}
            </p>
          )}
          {q.data && q.data.available && (
            <div className="space-y-4">
              <dl className="grid gap-2 text-sm sm:grid-cols-2">
                <Row label="Status">{q.data.status ?? '—'}</Row>
                <Row label="Inference time">
                  {q.data.inference_time != null ? `${q.data.inference_time.toFixed(2)}s` : '—'}
                </Row>
                <Row label="Our duration">{ourDurationSec.toFixed(1)}s</Row>
                <Row label="Endpoint">
                  <code className="font-mono text-xs">{q.data.fal_endpoint ?? '—'}</code>
                </Row>
                <Row label="Request">
                  <code className="font-mono text-xs">{q.data.fal_request_id ?? '—'}</code>
                </Row>
              </dl>

              {q.data.output?.description?.trim() && (
                <div>
                  <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-400">
                    Model reasoning
                  </h3>
                  <p className="whitespace-pre-wrap rounded-lg bg-ink-50 p-3 text-xs text-ink-600">
                    {q.data.output?.description}
                  </p>
                </div>
              )}

              {(() => {
                const videos = q.data.output?.videos?.length
                  ? q.data.output.videos
                  : q.data.output?.video
                    ? [q.data.output.video]
                    : []
                return videos.length > 0 ? (
                  <div>
                    <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-400">
                      Output video
                    </h3>
                    <div className="space-y-2">
                      {videos.map((v, i) => (
                        <div key={i} className="space-y-1">
                          <video src={v.url} controls playsInline className="w-full max-w-sm rounded-lg border border-ink-100 bg-black" />
                          <a href={v.url} target="_blank" rel="noreferrer" className="block text-xs text-brand-600 hover:underline">
                            {v.file_name ?? `video ${i + 1}`}
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null
              })()}

              {(q.data.output?.images?.length ?? 0) > 0 && (
                <div>
                  <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-400">
                    Output images
                  </h3>
                  <div className="space-y-1">
                    {q.data.output?.images?.map((img, i) => (
                      <div key={i} className="flex flex-wrap gap-x-3 text-xs text-ink-600">
                        <a
                          href={img.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-brand-600 hover:underline"
                        >
                          {img.file_name ?? `image ${i + 1}`}
                        </a>
                        {img.content_type && <span className="text-ink-400">{img.content_type}</span>}
                        {img.width && img.height && (
                          <span className="text-ink-400">
                            {img.width}×{img.height}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {Array.isArray(q.data.logs) && q.data.logs.length > 0 && (
                <div>
                  <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-400">
                    Logs
                  </h3>
                  <pre className="max-h-60 overflow-auto rounded-lg bg-ink-900/95 p-3 font-mono text-[10px] text-ink-100">
                    {q.data.logs
                      .map((l) => (typeof l === 'string' ? l : JSON.stringify(l)))
                      .join('\n')}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
